import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Input,
  Modal,
  SegmentedSetting,
  Select,
  Switch,
  Text,
  Textarea,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { knowledgeBaseService } from "services/knowledgeBaseService";
import { ServerConfig, serverService } from "services/serverService";
import { CreateWidgetRequest, widgetService } from "services/widgetService";
import {
  KbDocumentDto,
  KbDocumentStatus,
  KbWebSourceDto,
} from "types/knowledgeBase";

interface CreateWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWidgetCreated: (widgetId: string) => void;
}

const CreateWidgetModal = ({
  isOpen,
  onClose,
  onWidgetCreated,
}: CreateWidgetModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [buttonPosition, setButtonPosition] = useState(["bottom-right"]);
  const [displayType, setDisplayType] = useState(["popup"]);
  const [modalBackdrop, setModalBackdrop] = useState(true);
  const [requiresEndUserAuth, setRequiresEndUserAuth] = useState(true);
  const [servers, setServers] = useState<ServerConfig[]>([]);
  const [selectedServerIds, setSelectedServerIds] = useState<string[]>([]);
  const [loadingServers, setLoadingServers] = useState(false);
  const [webSources, setWebSources] = useState<KbWebSourceDto[]>([]);
  const [documents, setDocuments] = useState<KbDocumentDto[]>([]);
  const [selectedKbWebSourceIds, setSelectedKbWebSourceIds] = useState<
    string[]
  >([]);
  const [selectedKbDocumentIds, setSelectedKbDocumentIds] = useState<string[]>(
    [],
  );

  useEffect(() => {
    if (isOpen) {
      loadServers();
      loadWebSources();
      loadDocuments();
    }
  }, [isOpen]);

  const loadServers = async () => {
    setLoadingServers(true);
    try {
      const data = await serverService.getAllServers();
      setServers(data.filter((s) => s.isActive));
    } catch (err) {
      console.error("Failed to load servers:", err);
    } finally {
      setLoadingServers(false);
    }
  };

  const loadWebSources = async () => {
    try {
      const data = await knowledgeBaseService.getWebSources();
      setWebSources(data);
    } catch (err) {
      console.error("Failed to load web sources:", err);
    }
  };

  const loadDocuments = async () => {
    try {
      const data = await knowledgeBaseService.getDocuments({ pageSize: 500 });
      setDocuments(
        data.items.filter(
          (d: KbDocumentDto) => d.status === KbDocumentStatus.Indexed,
        ),
      );
    } catch (err) {
      console.error("Failed to load KB documents:", err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    const widgetName =
      (document.getElementById("widgetName") as HTMLInputElement)?.value || "";
    const widgetDescription =
      (document.getElementById("widgetDescription") as HTMLTextAreaElement)
        ?.value || "";
    const apiUrl =
      (document.getElementById("apiUrl") as HTMLInputElement)?.value || "";
    const buttonColor =
      (document.getElementById("buttonColor") as HTMLInputElement)?.value ||
      "#ffffff";
    const buttonBackgroundColor =
      (document.getElementById("buttonBackgroundColor") as HTMLInputElement)
        ?.value || "#2563eb";
    const buttonSize =
      (document.getElementById("buttonSize") as HTMLInputElement)?.value ||
      "3.5rem";
    const buttonMargin =
      (document.getElementById("buttonMargin") as HTMLInputElement)?.value ||
      "1rem";
    const iframeWidth =
      (document.getElementById("iframeWidth") as HTMLInputElement)?.value ||
      "400px";
    const iframeHeight =
      (document.getElementById("iframeHeight") as HTMLInputElement)?.value ||
      "600px";
    const svgIcon =
      (document.getElementById("svgIcon") as HTMLTextAreaElement)?.value || "";
    if (!widgetName) {
      setError(t("app.widgets.createModal.nameRequired"));
      return;
    }

    if (!selectedServerIds || selectedServerIds.length === 0) {
      setError(t("app.widgets.createModal.minServersError"));
      return;
    }

    if (selectedServerIds.length > 5) {
      setError(t("app.widgets.createModal.maxServersError"));
      return;
    }

    const requestData: CreateWidgetRequest = {
      name: widgetName,
      description: widgetDescription || undefined,
      apiUrl: apiUrl || undefined,
      buttonPosition: buttonPosition[0],
      buttonColor,
      buttonBackgroundColor,
      buttonSize,
      buttonMargin,
      iframeWidth,
      iframeHeight,
      displayType: displayType[0],
      modalBackdrop,
      requiresEndUserAuth,
      svgIcon: svgIcon || undefined,
      serverIds: selectedServerIds,
      kbDocumentIds:
        selectedKbDocumentIds.length > 0 ? selectedKbDocumentIds : undefined,
      kbWebSourceIds:
        selectedKbWebSourceIds.length > 0 ? selectedKbWebSourceIds : undefined,
    };

    setLoading(true);
    try {
      const created = await widgetService.createWidget(requestData);
      setSuccess(true);
      setTimeout(() => {
        onWidgetCreated(created.id!);
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("app.widgets.createModal.createFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    setSuccess(false);
    setButtonPosition(["bottom-right"]);
    setDisplayType(["popup"]);
    setModalBackdrop(true);
    setRequiresEndUserAuth(true);
    setSelectedServerIds([]);
    setSelectedKbWebSourceIds([]);
    setSelectedKbDocumentIds([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <Heading size="lg" color={{ base: "navy.700", _dark: "white" }}>
            {t("app.widgets.createModal.title")}
          </Heading>
          <Modal.CloseTrigger asChild>
            <Button variant="ghost" size="sm" p={0}>
              <MdClose className="h-6 w-6" />
            </Button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Flex
            mb={4}
            p={3}
            borderRadius="lg"
            bg={{ base: "red.100", _dark: "red.900" }}
            color={{ base: "red.700", _dark: "red.100" }}
          >
            <Text fontSize="sm">{error}</Text>
          </Flex>
        )}

        {success && (
          <Flex
            mb={4}
            p={3}
            borderRadius="lg"
            bg={{ base: "green.100", _dark: "green.900" }}
            color={{ base: "green.700", _dark: "green.100" }}
          >
            <Text fontSize="sm">
              {t("app.widgets.createModal.createSuccess")}
            </Text>
          </Flex>
        )}

        <form onSubmit={handleSubmit} id="create-widget-form">
          <Flex direction="column" gap={4}>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={4}
            >
              <Input
                label={t("app.widgets.createModal.nameLabel")}
                type="text"
                id="widgetName"
                placeholder={t("app.widgets.createModal.namePlaceholder")}
                required
              />
              <Select
                label={t("app.widgets.displayType")}
                items={[
                  {
                    label: t("app.widgets.displayTypes.popup"),
                    value: "popup",
                  },
                  {
                    label: t("app.widgets.displayTypes.sidebar"),
                    value: "sidebar",
                  },
                ]}
                value={displayType}
                onValueChange={(e: any) => setDisplayType(e.value)}
              />
            </Grid>

            <Textarea
              label={t("app.widgets.createModal.descriptionLabel")}
              id="widgetDescription"
              placeholder={t("app.widgets.createModal.descriptionPlaceholder")}
              rows={2}
            />

            <SegmentedSetting
              label={t("app.widgets.ux.requireCustomerAuth")}
              hint={t("app.widgets.ux.requireCustomerAuthHelp")}
              value={requiresEndUserAuth}
              onChange={setRequiresEndUserAuth}
            />

            <Flex direction="column" gap={2}>
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.widgets.createModal.serverLabel")}
              </Text>
              <Text
                fontSize="xs"
                color={{ base: "gray.600", _dark: "gray.400" }}
                mb={2}
              >
                {t("app.widgets.createModal.serverDescription")}
              </Text>
              {loadingServers ? (
                <Text
                  fontSize="sm"
                  color={{ base: "gray.500", _dark: "gray.400" }}
                >
                  {t("app.widgets.createModal.loadingServers")}
                </Text>
              ) : servers.length === 0 ? (
                <Text
                  fontSize="sm"
                  color={{ base: "red.600", _dark: "red.400" }}
                >
                  {t("app.widgets.createModal.noServers")}
                </Text>
              ) : (
                <Flex
                  direction="column"
                  gap={2}
                  p={3}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={{ base: "gray.200", _dark: "gray.700" }}
                >
                  {servers.map((server) => (
                    <HStack key={server.id} align="center" gap={2}>
                      <Switch
                        checked={selectedServerIds.includes(server.id!)}
                        disabled={
                          !selectedServerIds.includes(server.id!) &&
                          selectedServerIds.length >= 5
                        }
                        onCheckedChange={(e: any) => {
                          if (e.checked) {
                            if (selectedServerIds.length >= 5) {
                              setError(
                                t("app.widgets.createModal.maxServersError"),
                              );
                              return;
                            }
                            setSelectedServerIds([
                              ...selectedServerIds,
                              server.id!,
                            ]);
                            setError("");
                          } else {
                            setSelectedServerIds(
                              selectedServerIds.filter(
                                (id) => id !== server.id!,
                              ),
                            );
                          }
                        }}
                        colorPalette="blue"
                      />
                      <Flex direction="column" flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color={{ base: "navy.700", _dark: "white" }}
                        >
                          {server.name}
                        </Text>
                        {server.description && (
                          <Text
                            fontSize="xs"
                            color={{ base: "gray.600", _dark: "gray.400" }}
                          >
                            {server.description}
                          </Text>
                        )}
                      </Flex>
                    </HStack>
                  ))}
                </Flex>
              )}
              <Text
                fontSize="xs"
                color={{
                  base: selectedServerIds.length > 5 ? "red.600" : "gray.600",
                  _dark: selectedServerIds.length > 5 ? "red.400" : "gray.400",
                }}
              >
                {t("app.widgets.createModal.serversSelected", {
                  count: selectedServerIds.length,
                })}
              </Text>
            </Flex>

            {/* Knowledge Base section */}
            <Flex direction="column" gap={2}>
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.widgets.createModal.kbLabel")}
              </Text>

              {/* Documents list */}
              {documents.length > 0 && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    mb={2}
                    color={{ base: "gray.500", _dark: "gray.400" }}
                  >
                    Documents
                  </Text>
                  <Flex
                    direction="column"
                    gap={2}
                    p={3}
                    borderRadius="lg"
                    border="1px solid"
                    borderColor={{ base: "gray.200", _dark: "gray.700" }}
                  >
                    {documents.map((doc) => {
                      const isSelected =
                        doc.isGlobal || selectedKbDocumentIds.includes(doc.id);
                      return (
                        <HStack key={doc.id} align="center" gap={2}>
                          <Switch
                            checked={isSelected}
                            disabled={doc.isGlobal}
                            onCheckedChange={(e: any) => {
                              if (doc.isGlobal) return;
                              if (e.checked) {
                                setSelectedKbDocumentIds([
                                  ...selectedKbDocumentIds,
                                  doc.id,
                                ]);
                              } else {
                                setSelectedKbDocumentIds(
                                  selectedKbDocumentIds.filter(
                                    (id) => id !== doc.id,
                                  ),
                                );
                              }
                            }}
                            colorPalette="blue"
                          />
                          <Flex direction="column" flex={1}>
                            <HStack gap={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color={{ base: "navy.700", _dark: "white" }}
                              >
                                {doc.fileName}
                              </Text>
                              {doc.isGlobal && (
                                <Badge
                                  colorPalette="purple"
                                  size="sm"
                                  variant="subtle"
                                >
                                  Global
                                </Badge>
                              )}
                            </HStack>
                          </Flex>
                        </HStack>
                      );
                    })}
                  </Flex>
                </Box>
              )}

              {/* Divider between documents and web sources */}
              {documents.length > 0 && webSources.length > 0 && (
                <Box
                  my={1}
                  borderBottom="1px solid"
                  borderColor={{ base: "gray.200", _dark: "gray.600" }}
                />
              )}

              {/* Web sources list */}
              {webSources.length > 0 && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    mb={2}
                    color={{ base: "gray.500", _dark: "gray.400" }}
                  >
                    Web Sources
                  </Text>
                  <Flex
                    direction="column"
                    gap={2}
                    p={3}
                    borderRadius="lg"
                    border="1px solid"
                    borderColor={{ base: "gray.200", _dark: "gray.700" }}
                  >
                    {webSources.map((source) => {
                      const isSelected =
                        source.isGlobal ||
                        selectedKbWebSourceIds.includes(source.id);
                      return (
                        <HStack key={source.id} align="center" gap={2}>
                          <Switch
                            checked={isSelected}
                            disabled={source.isGlobal}
                            onCheckedChange={(e: any) => {
                              if (source.isGlobal) return;
                              if (e.checked) {
                                setSelectedKbWebSourceIds([
                                  ...selectedKbWebSourceIds,
                                  source.id,
                                ]);
                              } else {
                                setSelectedKbWebSourceIds(
                                  selectedKbWebSourceIds.filter(
                                    (id) => id !== source.id,
                                  ),
                                );
                              }
                            }}
                            colorPalette="blue"
                          />
                          <Flex direction="column" flex={1}>
                            <HStack gap={2}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color={{ base: "navy.700", _dark: "white" }}
                              >
                                {source.name}
                              </Text>
                              {source.isGlobal && (
                                <Badge
                                  colorPalette="purple"
                                  size="sm"
                                  variant="subtle"
                                >
                                  Global
                                </Badge>
                              )}
                            </HStack>
                          </Flex>
                        </HStack>
                      );
                    })}
                  </Flex>
                </Box>
              )}
            </Flex>
          </Flex>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <HStack gap={3}>
          <Button
            type="button"
            onClick={handleClose}
            variant="subtle"
            colorPalette="gray"
            flex={1}
          >
            {t("app.widgets.createModal.cancel")}
          </Button>
          <Button
            type="submit"
            form="create-widget-form"
            disabled={loading}
            variant="solid"
            colorPalette="blue"
            flex={1}
          >
            {loading
              ? t("app.widgets.createModal.creating")
              : t("app.widgets.createModal.create")}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateWidgetModal;
